import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CreateSimilarityReportCommand,
  CreateSimilarityReportIncludedVariableCommand,
  CreateSimilarityReportOpenEndsCommand,
  DownloadSimilarityReportCommand,
  DownloadSimilarityReportOpenEndsCommand,
  ISimilarityReportClient,
  SaveSimilarityConfigCommand,
  SimilarityReportClient,
  SimilarityReportMode,
  SimilarityReportRunType,
  SimilarityReportStatusDto,
  SimilarityReportStatuses,
  UpdateSimilarityReportHistoryCommand,
  VariableCategoryDto,
} from "app/ApiClient";
import { IState } from "slices";
import { LoadingState } from "types";
import { initialSimilarityReportState, SimilarityReportState } from "./similarityReportState";

const similarityReportClient: ISimilarityReportClient = new SimilarityReportClient(process.env.REACT_APP_API_URL);

export const saveSimilarityFormData = createAsyncThunk(
  "similarityReport/saveSimilarityConfigurations",
  async (command: SaveSimilarityConfigCommand, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.saveSimilarityConfigurations(command);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAllSavedSimilarityConfigNames = createAsyncThunk(
  "similarityReport/getAllSavedSimilarityConfigs",
  async ({projectId, versionId}: {projectId: number, versionId: string}, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.getAllSavedSimilarityConfigs(projectId, versionId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getSavedSimilarityConfigsByName = createAsyncThunk(
  "similarityReport/getSavedSimilarityConfigsByName",
  async ({projectId, versionId, configName}: {projectId: number, versionId: string, configName:string}, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.getSavedSimilarityConfigsByName(projectId, versionId, configName);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);


export const getSimilarityReport = createAsyncThunk(
  "similarityReport/getSimilarityReport",
  async (command: DownloadSimilarityReportCommand, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.downloadSimilarityReport(command);
      if (!result.urls || result.urls.length === 0) {
        throw new Error("No files to download");
      }
      return result.urls;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getSimilarityReportOpenEnds = createAsyncThunk(
  "similarityReport/getSimilarityReportOpenEnds",
  async (command: DownloadSimilarityReportOpenEndsCommand, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.downloadSimilarityReportOpenEnds(command);
      if (!result.urls || result.urls.length === 0) {
        throw new Error("No files to download");
      }
      return result.urls;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createSimilarityReport = createAsyncThunk(
  "similarityReport/createSimilarityReport",
  async ({ command, selectedCategories }: { command: CreateSimilarityReportCommand, selectedCategories?: VariableCategoryDto[] }, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.createSimilarityReport(command);
      if(selectedCategories) {
        setSimCheckerSelectedVariableCategories(selectedCategories ?? []);
      }
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createSimilarityReportIncludedVariable = createAsyncThunk(
  "similarityReport/createSimilarityReportIncludedVariable",
  async ({ command, selectedCategories }: { command: CreateSimilarityReportIncludedVariableCommand, selectedCategories?: VariableCategoryDto[] }, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.createSimilarityReportIncludedVariables(command);
      if(selectedCategories) {
        setSimCheckerSelectedVariableCategories(selectedCategories ?? []);
      }
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createSimilarityReportOpenEnds = createAsyncThunk(
  "similarityReport/createSimilarityReportOpenEnds",
  async ({ command }: { command: CreateSimilarityReportOpenEndsCommand }, { rejectWithValue }) => {
    try {
      return await similarityReportClient.createSimilarityReportOpenEnds(command);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getSimilarityReportStatus = createAsyncThunk(
  "similarityReport/getSimilarityReportStatus",
  async (token: string, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.getSimilarityReportStatus(token);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getSimilarityReportOpenEndsStatus = createAsyncThunk(
  "similarityReport/getSimilarityReportOpenEndsStatus",
  async (token: string, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.getSimilarityReportOpenEndsStatus(token);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getSimilarityReportHistory = createAsyncThunk(
  "similarityReport/getSimilarityReportHistory",
  async ({ projectId, modificationId, versionId } : { projectId: number, modificationId: number, versionId: string | null}, { rejectWithValue, dispatch }) => {
    try {
      const result = await similarityReportClient.getSimilarityReportHistory(projectId, modificationId, versionId);
      if(result.token) {         
        dispatch(resetSimilarityReportStages());
      }
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteSimilarityReportHistory = createAsyncThunk(
  "similarityReport/deleteSimilarityReportHistory",
  async ({ projectId, modificationId, versionId } : { projectId: number, modificationId: number, versionId: string }, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.deleteSimilarityReportHistory(projectId, modificationId, versionId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateSimilarityReportHistoryStatus = createAsyncThunk(
  "similarityReport/updateSimilarityReportStatus",
  async (command : UpdateSimilarityReportHistoryCommand, { rejectWithValue }) => {
    try {
      const result = await similarityReportClient.updateSimilarityReportStatus(command);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const similarityReportSlice = createSlice({
  name: "similarityReport",
  initialState: initialSimilarityReportState,
  reducers: {
    resetSimilarityReport: () => initialSimilarityReportState,
    setCustomSimilarityReportDialog: (state, { payload }: PayloadAction<boolean>) => {
      state.isCustomReportDialogOpended = payload;
    },
    setSimilarityProgressDialogOpened: (state, { payload }: PayloadAction<boolean>) => {
      state.isSimilarityProgressDialogOpened = payload;
    },
    setSimilarityReportMode: (state, { payload }: PayloadAction<SimilarityReportMode>) => {
      state.similarityReportMode = payload;
    },
    resetSimilarityReportStages: (state) => {
      state.stages = initialSimilarityReportState.stages;
    },
    setSimilarityReportStages: (state, { payload }: PayloadAction<SimilarityReportStatusDto>) => {
      state.stages = payload;
    },
    setSimCheckerFormDataRequest: (state, { payload }: PayloadAction<CreateSimilarityReportCommand>) => {
      state.similarityFormRequest = {...payload};
    },
    setSimCheckerSelectedVariableCategories: (state, { payload }: PayloadAction<VariableCategoryDto[]>) => {
      state.selectedVariableCategories = payload;
    },
    resetSimCheckerFormConfigs: (state) => {
      state.saveSimilarityFormDataLoading = LoadingState.idle;
      state.similarityFormConfigLoading = LoadingState.idle;
      state.similarityFormConfig = undefined;
    },
  },
  extraReducers: (builder) => {
    // getSimilarityReport
    builder.addCase(getSimilarityReport.pending, (state) => {
      state.getReportLoading = LoadingState.pending;
    });
    builder.addCase(getSimilarityReport.fulfilled, (state, { payload }) => {
      state.getReportLoading = LoadingState.succeed;
      state.urls = payload;
      state.isSimilarityProgressDialogOpened = false;
      state.status = SimilarityReportStatuses.Complete;
    });
    builder.addCase(getSimilarityReport.rejected, (state, action) => {
      state.getReportLoading = LoadingState.failed;
      state.errorMessage = action.error.message;
      state.status = SimilarityReportStatuses.Failed;
    });

    // getSimilarityReportOpenEnds
    builder.addCase(getSimilarityReportOpenEnds.pending, (state) => {
      state.getReportLoading = LoadingState.pending;
    });
    builder.addCase(getSimilarityReportOpenEnds.fulfilled, (state, { payload }) => {
      state.getReportLoading = LoadingState.succeed;
      state.urls = payload;
      state.isSimilarityProgressDialogOpened = false;
      state.status = SimilarityReportStatuses.Complete;
    });
    builder.addCase(getSimilarityReportOpenEnds.rejected, (state, action) => {
      state.getReportLoading = LoadingState.failed;
      state.errorMessage = action.error.message;
      state.status = SimilarityReportStatuses.Failed;
    });

    // createSimilarityReport
    builder.addCase(createSimilarityReport.pending, (state) => {
      state.createReportLoading = LoadingState.pending;
      state.getReportLoading = LoadingState.idle;
      state.errorMessage = "";
      state.statusMessage = "";
      state.getReportStatusLoading = LoadingState.idle;
      state.status = undefined;
      state.token = null;
      state.urls = [];
    });
    builder.addCase(createSimilarityReport.fulfilled, (state, { payload }) => {
      // this condition required if user leave the component and reset report state
      if (state.createReportLoading === LoadingState.pending) {
        state.createReportLoading = LoadingState.succeed;
        state.token = payload;
        // if token is null then it report is already in cache and it's necessary to mark status as complete
        if (payload === null) {
          state.getReportStatusLoading = LoadingState.succeed;
          state.status = SimilarityReportStatuses.Complete;
        }
      }
    });
    builder.addCase(createSimilarityReport.rejected, (state, action) => {
      state.createReportLoading = LoadingState.failed;
      state.errorMessage = action.error.message;
      state.status = SimilarityReportStatuses.Failed;
      state.isSimilarityProgressDialogOpened = false;
    });

    // createSimilarityReportIncludedVariable
    builder.addCase(createSimilarityReportIncludedVariable.pending || createSimilarityReport.pending, (state) => {
      state.createReportLoading = LoadingState.pending;
      state.getReportLoading = LoadingState.idle;
      state.errorMessage = "";
      state.statusMessage = "";
      state.getReportStatusLoading = LoadingState.idle;
      state.status = undefined;
      state.token = null;
      state.urls = [];
    });
    builder.addCase(createSimilarityReportIncludedVariable.fulfilled, (state, { payload }) => {
      // this condition required if user leave the component and reset report state
      if (state.createReportLoading === LoadingState.pending) {
        state.createReportLoading = LoadingState.succeed;
        state.token = payload;
        // if token is null then it report is already in cache and it's necessary to mark status as complete
        if (payload === null) {
          state.getReportStatusLoading = LoadingState.succeed;
          state.status = SimilarityReportStatuses.Complete;
        }
      }
    });
    builder.addCase(createSimilarityReportIncludedVariable.rejected, (state, action) => {
      state.createReportLoading = LoadingState.failed;
      state.errorMessage = action.error.message;
      state.status = SimilarityReportStatuses.Failed;
    });

    // createSimilarityReportOpenEnds
    builder.addCase(createSimilarityReportOpenEnds.pending || createSimilarityReport.pending, (state) => {
      state.createReportLoading = LoadingState.pending;
      state.getReportLoading = LoadingState.idle;
      state.errorMessage = "";
      state.statusMessage = "";
      state.getReportStatusLoading = LoadingState.idle;
      state.status = undefined;
      state.token = null;
      state.urls = [];
    });
    builder.addCase(createSimilarityReportOpenEnds.fulfilled, (state, { payload }) => {
      // this condition required if user leave the component and reset report state
      if (state.createReportLoading === LoadingState.pending) {
        state.createReportLoading = LoadingState.succeed;
        state.token = payload;
      }
    });
    builder.addCase(createSimilarityReportOpenEnds.rejected, (state, action) => {
      state.createReportLoading = LoadingState.failed;
      state.errorMessage = action.error.message;
      state.status = SimilarityReportStatuses.Failed;
    });

    // getSimilarityReportStatus
    builder.addCase(getSimilarityReportStatus.pending, (state) => {
      state.getReportStatusLoading = LoadingState.pending;
      state.getReportLoading = LoadingState.pending;
    });
    builder.addCase(getSimilarityReportStatus.fulfilled, (state, { payload }) => {
      // this condition required if user leave the component and reset report state
        state.getReportStatusLoading = LoadingState.succeed;
        state.status = payload.status;
        state.statusMessage = payload.message;

        // update existing stages with save initial stages
        state.stages = state.stages
          ? {
              ...payload,
              subgroups: !!payload.subgroups.length
                ? state.stages.subgroups.map((item) => payload.subgroups.find(({ name }) => name === item.name) || item)
                : state.stages.subgroups,
            }
          : payload;

        if (payload.status === SimilarityReportStatuses.Failed) {
          state.status = SimilarityReportStatuses.Failed;
          state.errorMessage = payload.message;
        }
    });
    builder.addCase(getSimilarityReportStatus.rejected, (state, action) => {
      state.getReportStatusLoading = LoadingState.failed;
      state.status = SimilarityReportStatuses.Failed;
      state.errorMessage = action.error.message;
    });

    // getSimilarityReportOpenEndsStatus
    builder.addCase(getSimilarityReportOpenEndsStatus.pending, (state) => {
      state.getReportStatusLoading = LoadingState.pending;
      state.getReportLoading = LoadingState.pending;
    });
    builder.addCase(getSimilarityReportOpenEndsStatus.fulfilled, (state, { payload }) => {
      // this condition required if user leave the component and reset report state
        state.getReportStatusLoading = LoadingState.succeed;
        state.status = payload.status;
        state.statusMessage = payload.message;
        // update existing stages with save initial stages
        state.stages = state.stages
          ? {
              ...payload,
              subgroups: !!payload.subgroups.length
                ? state.stages.subgroups.map((item) => payload.subgroups.find(({ name }) => name === item.name) || item)
                : state.stages.subgroups,
            }
          : payload;

        if (payload.status === SimilarityReportStatuses.Failed) {
          state.status = SimilarityReportStatuses.Failed;
          state.errorMessage = payload.message;
        }
    });
    builder.addCase(getSimilarityReportOpenEndsStatus.rejected, (state, action) => {
      state.getReportStatusLoading = LoadingState.failed;
      state.status = SimilarityReportStatuses.Failed;
      state.errorMessage = action.error.message;
    });

   // getSimilarityReportHistory
   builder.addCase(getSimilarityReportHistory.fulfilled, (state, { payload }) => {
      state.token = payload?.token;
      state.status = payload?.status;
      state.similarityReportMode = payload?.similarityReportMode;
      state.similarityFormRequest = {
        id:payload?.projectId ?? 0,
        batteries: payload?.similarityReportFormData?.batteries ?? [],
        encodingVersionId: payload?.encodingVersionId ?? "",
        runType: payload?.similarityReportFormData?.runType ?? SimilarityReportRunType.Quick,
        zScore: payload?.similarityReportFormData?.zScore ?? 0,
        abnormalitySelectivity: payload?.similarityReportFormData?.abnormalitySelectivity ?? 0,
        categories:  payload?.similarityReportFormData?.variable ? payload?.similarityReportFormData?.categories: [],
        variable: payload?.similarityReportFormData?.variable,
      }
   });

  // getSavedSimilarityConfigNames
  builder.addCase(getAllSavedSimilarityConfigNames.pending, (state) => {
    state.savedSimilarityConfigNamesLoading = LoadingState.pending;
  });
  builder.addCase(getAllSavedSimilarityConfigNames.fulfilled, (state, { payload }) => {
    state.savedSimilarityConfigNamesLoading = LoadingState.succeed;
    state.savedSimilarityConfigNames = payload;
  });
  builder.addCase(getAllSavedSimilarityConfigNames.rejected, (state) => {
    state.savedSimilarityConfigNamesLoading = LoadingState.failed;
  });

  // getSavedSimilarityConfigFormData
  builder.addCase(getSavedSimilarityConfigsByName.pending, (state) => {
    state.similarityFormConfigLoading = LoadingState.pending;
  });
  builder.addCase(getSavedSimilarityConfigsByName.fulfilled, (state, { payload }) => {
    state.similarityFormConfigLoading = LoadingState.succeed;
    state.similarityFormConfig = payload;
  });
  builder.addCase(getSavedSimilarityConfigsByName.rejected, (state) => {
    state.similarityFormConfigLoading = LoadingState.failed;
  });

  // Save Similarity form configurations
  builder.addCase(saveSimilarityFormData.pending, (state) => {
    state.saveSimilarityFormDataLoading = LoadingState.pending;
  });
  builder.addCase(saveSimilarityFormData.fulfilled, (state, { meta:  { arg : { similarityConfigsName } }}) => {
    state.saveSimilarityFormDataLoading = LoadingState.succeed;
    state.savedSimilarityConfigName = similarityConfigsName;
  });
  builder.addCase(saveSimilarityFormData.rejected, (state) => {
    state.saveSimilarityFormDataLoading = LoadingState.failed;
  });
  },
});

export const {
  resetSimilarityReport,
  setCustomSimilarityReportDialog,
  setSimilarityProgressDialogOpened,
  resetSimilarityReportStages,
  setSimilarityReportStages,
  setSimCheckerFormDataRequest,
  setSimCheckerSelectedVariableCategories,
  resetSimCheckerFormConfigs,
  setSimilarityReportMode
} = similarityReportSlice.actions;
export const similarityReportSelector = (state: IState): SimilarityReportState => state.similarityReport;
export default similarityReportSlice.reducer;
